import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // Check if we have a token in Chrome storage
      if (typeof chrome !== 'undefined' && chrome.storage) {
        const { accessToken } = await chrome.storage.local.get('accessToken');
        if (accessToken) {
          const { data } = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          setUser(data);
        }
      } else {
        // Fallback to cookies for web version
        const { data } = await axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true
        });
        setUser(data);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    
    // Store in Chrome storage if available
    if (typeof chrome !== 'undefined' && chrome.storage) {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      // Extract token from cookie or response
      await chrome.storage.local.set({ accessToken: 'session_active' });
    }
    
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(
      `${API_URL}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    
    if (typeof chrome !== 'undefined' && chrome.storage) {
      await chrome.storage.local.set({ accessToken: 'session_active' });
    }
    
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    if (typeof chrome !== 'undefined' && chrome.storage) {
      await chrome.storage.local.remove('accessToken');
    }
    
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};